/**
 * DocumentPanel — a Word or Excel file Livi created, previewed in the center
 * panel with a Download button. Published by the chat agent's save_document /
 * create_document tools; also opened from a file chip in the chat and from the
 * Livi Files page. Livi no longer makes PDFs; the PDF preview stays so PDFs
 * made before that change still open. See .claude/references/livi-documents.md (ADR-001, D4).
 *
 * Props come from the backend's panel_props: { documentId, title, format,
 * filename, s3Key, bucket, sizeBytes, created, producer, sourceCount }. Only
 * `documentId` is required: a chip knows just the id and title, so the record is
 * fetched when the S3 key is missing, and the bucket is resolved for the case.
 * No signed URL is ever stored; the browser signs when it needs the bytes.
 */

import React, { useCallback, useContext, useState } from 'react';
import useSWR from 'swr';
import PdfViewer from '../ui/PDFViewer';
import XlsxPreview from './XlsxPreview';
import DocxPreview from './DocxPreview';
import UserProfileContext from '../../context/userprofilecontext';
import useGetSignedUrl from '../../api/signedurl';
import { useCaseBucketName } from '../../api/casebucket';
import { useLiviDocument } from '../../api/liviDocuments';
import {
  DOCUMENT_FORMATS, fetchDocumentBytes, saveBytesAsFile, withExtension,
} from '../../utils/caseObjectDownload';
import { formatDateTime, formatFileSize } from '../../utils/formatters';
import styles from './DocumentPanel.module.css';

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sourceLine = (count) => {
  const n = Number(count) || 0;
  if (n === 0) return null;
  return `Built from ${n} ${n === 1 ? 'source' : 'sources'}`;
};

export default function DocumentPanel(props) {
  const { documentId } = props;
  const { userProfile } = useContext(UserProfileContext) || {};
  const caseId = userProfile?.currentCase?.caseID;

  // A chip carries only the id: fetch the record for the key, filename and details.
  const needsRecord = !props.s3Key;
  const { data: record, error: recordError } = useLiviDocument(documentId, needsRecord);
  const doc = { ...(record || {}), ...Object.fromEntries(Object.entries({
    title: props.title,
    format: props.format,
    filename: props.filename,
    s3Key: props.s3Key,
    sizeBytes: props.sizeBytes,
    created: props.created,
    sourceCount: props.sourceCount,
  }).filter(([, v]) => v !== undefined && v !== null)) };

  const { bucketName: caseBucket } = useCaseBucketName(caseId, !props.bucket);
  const bucket = props.bucket || caseBucket;
  const format = (doc.format || '').toLowerCase();
  const formatInfo = DOCUMENT_FORMATS[format];
  const { data: signedUrl, error: signError } = useGetSignedUrl(bucket, doc.s3Key, !!(bucket && doc.s3Key));

  // Word and Excel previews read the bytes; keyed on the document (its file never
  // changes), so the download reuses them. The URL is read at fetch time.
  const wantsBytes = !!(signedUrl && (format === 'docx' || format === 'xlsx'));
  const {
    data: bytes, error: bytesError, mutate: refetchBytes, isValidating,
  } = useSWR(
    wantsBytes ? ['LIVI_DOCUMENT_BYTES', documentId] : null,
    () => fetchDocumentBytes(signedUrl, format),
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false, shouldRetryOnError: false }
  );

  const [downloadState, setDownloadState] = useState(null); // null | 'working' | 'error'
  const filename = withExtension(doc.filename || doc.title, format);

  const download = useCallback(async () => {
    if (!formatInfo) return;
    setDownloadState('working');
    try {
      const buffer = bytes || await fetchDocumentBytes(signedUrl, format);
      saveBytesAsFile(buffer, filename, formatInfo.mime);
      setDownloadState(null);
    } catch (err) {
      setDownloadState('error');
    }
  }, [bytes, signedUrl, format, formatInfo, filename]);

  const created = toDate(doc.created);
  const meta = [
    formatInfo?.label,
    formatFileSize(doc.sizeBytes),
    created ? formatDateTime(created) : null,
    sourceLine(doc.sourceCount),
  ].filter(Boolean);

  const unavailable = recordError || signError || (!needsRecord && !formatInfo) || (record && !formatInfo);

  let body;
  if (unavailable) {
    body = (
      <p className={styles.errorText} role="alert">
        This file can’t be opened right now. It may have been deleted or archived.
      </p>
    );
  } else if (!signedUrl || !formatInfo) {
    body = <p className={styles.mutedText}>Loading the file…</p>;
  } else if (format === 'pdf') {
    body = (
      <div className={styles.pdfFrame}>
        <PdfViewer url={signedUrl} documentKey={`livi-document:${documentId}`} documentName={filename} />
      </div>
    );
  } else if (bytesError) {
    body = (
      <div className={styles.errorBlock} role="alert">
        <p className={styles.errorText}>The file could not be loaded for preview.</p>
        <button type="button" className={styles.secondaryBtn} onClick={() => refetchBytes()} disabled={isValidating}>
          Try again
        </button>
      </div>
    );
  } else if (!bytes) {
    body = <p className={styles.mutedText}>Loading the file…</p>;
  } else if (format === 'xlsx') {
    body = <XlsxPreview buffer={bytes} />;
  } else {
    body = <DocxPreview buffer={bytes} />;
  }

  return (
    <section className={styles.panel} aria-label={doc.title ? `Document: ${doc.title}` : 'Document'}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>{doc.title || 'Document'}</h2>
          {meta.length > 0 && <p className={styles.meta}>{meta.join(' · ')}</p>}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={download}
            disabled={!signedUrl || !formatInfo || downloadState === 'working'}
            aria-label={`Download ${filename}`}
          >
            {downloadState === 'working' ? 'Downloading…' : 'Download'}
          </button>
        </div>
      </header>
      {downloadState === 'error' && (
        <p className={styles.errorText} role="alert">
          The download didn’t work. The link may have expired; please try again.
        </p>
      )}
      <div className={styles.body}>{body}</div>
    </section>
  );
}
